import { Button } from "@repo/ui";
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { useMarkCouponsPrinted, useRegistrationDetail } from "../../lib/queries/use-registration";

// Configuration constants for coupon positioning
// Adjust these values after printing test pages and measuring alignment
const COUPON_CONFIG = {
  WIDTH: "10.5cm",
  HEIGHT: "29.7cm",
  TOP_SECTION_OFFSET: "3.75cm", // Starting position for first section from top
  BOTTOM_SECTION_OFFSET: "13.7cm", // Starting position for second section from top
  LEFT_MARGIN: "0.8cm", // Left offset for all fields
  LINE_HEIGHT: "1.0cm", // Spacing between lines within each field
  FIELD_SPACING: "0.63cm", // Spacing between different fields
  MAX_WIDTH: "8.5cm", // Maximum width for text fields
  FONT_SIZE: "14px", // Base font size
  THB_AMOUNT_PER_COUPON: 300000,
} as const;

// Format phone number as 000-000-0000
const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");

  // Format as 000-000-0000
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  // Return original if not 10 digits
  return phone;
};

export default function PrintCouponPage() {
  const { id } = useParams();
  const { data, isLoading, isError } = useRegistrationDetail(id);
  const markCouponsPrinted = useMarkCouponsPrinted();
  const fieldsRef = useRef<HTMLDivElement[]>([]);

  const item = data?.item;

  useEffect(() => {
    // Mark coupons as printed when component loads
    if (!isLoading && item && id) {
      markCouponsPrinted.mutate({ id: Number(id) });
    }
  }, [isLoading, item, id, markCouponsPrinted]);

  useEffect(() => {
    // Scale down text if it exceeds max width
    const maxWidthPx = parseFloat(COUPON_CONFIG.MAX_WIDTH) * 37.795275591; // Convert cm to px (1cm = 37.795275591px)

    fieldsRef.current.forEach((field) => {
      if (field) {
        const textWidth = field.scrollWidth;
        if (textWidth > maxWidthPx) {
          const scale = maxWidthPx / textWidth;
          field.style.transform = `scaleX(${scale})`;
        }
      }
    });
  }, [item]);

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  if (isError || !item) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Registration not found</p>
      </div>
    );
  }

  // Calculate number of coupons (1 coupon per 50,000)
  const valueAmount = Number(item.value_amount || "0");
  const numCoupons = Math.floor(valueAmount / COUPON_CONFIG.THB_AMOUNT_PER_COUPON);

  if (numCoupons === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>No coupons available for this registration</p>
      </div>
    );
  }

  const coupons = Array.from({ length: numCoupons }, (_, i) => i + 1);

  // Helper function to render a section of fields at a specific vertical offset
  const renderSection = (topOffset: string, sectionIndex: number) => {
    let currentTop = topOffset;
    const fields = [
      `${item.name} ${item.surname}`, // Name
      item.address || "-", // Address
      formatPhoneNumber(item.phone), // Phone
      item.developer || "-", // Developer
    ];

    return fields.map((fieldValue, fieldIndex) => {
      const refIndex = sectionIndex * fields.length + fieldIndex;
      const element = (
        <div
          key={fieldIndex}
          ref={(el) => {
            if (el) fieldsRef.current[refIndex] = el;
          }}
          className="coupon-field"
          style={{
            position: "absolute",
            top: currentTop,
            left: COUPON_CONFIG.LEFT_MARGIN,
            maxWidth: COUPON_CONFIG.MAX_WIDTH,
            fontSize: COUPON_CONFIG.FONT_SIZE,
            whiteSpace: "nowrap",
          }}
        >
          {fieldValue}
        </div>
      );

      // Calculate next position
      currentTop = `calc(${currentTop} + ${COUPON_CONFIG.LINE_HEIGHT} + ${COUPON_CONFIG.FIELD_SPACING})`;

      return element;
    });
  };

  return (
    <>
      <style>{`
        .coupon-field {
          transform-origin: left top;
        }
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          .coupon-page {
            page-break-after: always;
            page-break-inside: avoid;
            position: relative;
          }
          .coupon-page:last-child {
            page-break-after: auto;
          }
          .no-print {
            display: none;
          }
          .coupon-field {
            max-width: ${COUPON_CONFIG.MAX_WIDTH};
            display: inline-block;
          }
        }
        @page {
          size: ${COUPON_CONFIG.WIDTH} ${COUPON_CONFIG.HEIGHT};
          margin: 0;
        }
      `}</style>

      <div className="no-print p-4 bg-muted border-b flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Ready to print {numCoupons} coupon{numCoupons > 1 ? "s" : ""} for {item.name}{" "}
          {item.surname}
        </p>
        <Button onClick={handlePrint} className="ml-4">
          Print Coupons
        </Button>
      </div>

      {coupons.map((couponNum, couponIndex) => (
        <div
          key={couponNum}
          className="coupon-page"
          style={{
            width: COUPON_CONFIG.WIDTH,
            height: COUPON_CONFIG.HEIGHT,
          }}
        >
          {/* Top section */}
          {renderSection(COUPON_CONFIG.TOP_SECTION_OFFSET, couponIndex * 2)}

          {/* Bottom section */}
          {renderSection(COUPON_CONFIG.BOTTOM_SECTION_OFFSET, couponIndex * 2 + 1)}
        </div>
      ))}
    </>
  );
}
